import { HTMLAttributes, memo, useCallback } from 'react'

import { Check, Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  FormControl,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import {
  OrgTeam,
  OrgEnvironmentRoleRamMemberRolesEnum,
  OrgTeamRoleRamMemberInsert,
  EnvironmentsEnvironmentIdMembersMemberIdGetRequest,
} from '@nativewaves/nw-cloud-sdk/org'
import { useInfiniteQuery, useQuery, useMutation } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import {
  ListBoxInfinite,
  filterOptionsWithAutocompleteFilter,
} from '@shared/components/MaterialUIEnhancements/AutoComplete'
import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { appearance } from '@shared/utils/support'
import { useEnvironmentMemberCreateMutation } from 'hooks/mutations/org'
import { teamsOverviewRoute } from 'pages/NWPlatform/Environment'
import { OrgAPI } from 'services/api'
import { environmentQueryKeys, teamQueryKeys } from 'services/queryKeys'
import { stopPropagate } from 'utils'

type FormStructure = Omit<OrgTeamRoleRamMemberInsert, 'roles'> & {
  role: OrgEnvironmentRoleRamMemberRolesEnum
}

const TeamDialogContentRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { environmentId } = teamsOverviewRoute.useParams()

  const handleClose = useCloseHandler()

  const environmentQuery = useQuery(environmentQueryKeys.detail({ environmentId }))
  const { data: environment } = environmentQuery

  const teamsQuery = useInfiniteQuery({
    ...teamQueryKeys.list({ organizationId: environment?.organizationId as string }),
    enabled: !!environment?.organizationId,
  })
  const teams = teamsQuery.data?.pages.flatMap((page) => page.items)

  const { mutate: addMember, ...addMemberMutation } = useEnvironmentMemberCreateMutation()
  const memberGetMutation = useMutation({
    mutationFn: (params: EnvironmentsEnvironmentIdMembersMemberIdGetRequest) =>
      OrgAPI.environments.environmentsEnvironmentIdMembersMemberIdGet(params),
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
  })

  const {
    control,
    handleSubmit,
    setError: setFormError,
  } = useForm<FormStructure>({
    defaultValues: {
      role: 'Read',
    },
  })

  const handleFormSubmit = useCallback(
    async (data: FormStructure) => {
      if (!data.teamId) {
        setFormError('teamId', { message: t('domain:EnvironmentSetting.teamNotSelected') })
        return
      }

      try {
        const existingTeam = await memberGetMutation.mutateAsync({ environmentId, memberId: data.teamId })
        if (existingTeam) {
          enqueueSnackbar(t('domain:My.Organization.teamIsAlreadyAssigned'), {
            variant: 'error',
          })
          setFormError('teamId', { message: t('domain:My.Organization.teamIsAlreadyAssigned') }, { shouldFocus: true })
          return
        }
      } catch (_) {}

      addMember(
        {
          environmentId,
          orgEnvironmentRoleRamMemberInsert: {
            ...data,
            // TODO: Disable Sets in API Sdk since they don't work properly
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            roles: [data.role],
          },
        },
        {
          onSuccess: () => {
            setTimeout(() => {
              handleClose()
            }, 300)
          },
          onError: () => {
            setFormError(
              'teamId',
              { message: t('domain:My.Organization.teamIsAlreadyAssigned') },
              { shouldFocus: true },
            )
          },
        },
      )
    },
    [addMember, environmentId, setFormError, t, memberGetMutation, enqueueSnackbar, handleClose],
  )

  return (
    <DialogFormContent
      sx={{ minWidth: 480 }}
      title={`${t('domain:EnvironmentSetting.associateTeamsWith')} ${environment?.name}`}
      description={t('domain:EnvironmentSetting.addTeamInstructions')}
      onSubmit={stopPropagate(handleSubmit(handleFormSubmit))}
      Illustration={null}
      SaveButton={
        <AnimatePresence mode="wait">
          <LoadingButton
            sx={{ ml: 'auto' }}
            key="e-mail"
            component={motion.button}
            variants={appearance}
            initial="hidden"
            animate="visible"
            exit="hidden"
            endIcon={addMemberMutation.isSuccess ? <Check /> : <Save />}
            loading={addMemberMutation.isPending}
            disabled={addMemberMutation.isSuccess}
            loadingPosition="end"
            type="submit"
            variant="text"
          >
            Save
          </LoadingButton>
        </AnimatePresence>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, mt: 3 }}>
        {teamsQuery.isLoading ? (
          <Skeleton sx={{ flex: 1.5, alignSelf: 'stretch' }} />
        ) : (
          <Controller
            control={control}
            name="teamId"
            rules={{
              required: true,
            }}
            render={({ field, fieldState, formState }) => (
              <Autocomplete
                sx={{ flex: 1.5 }}
                {...field}
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                filterOptions={filterOptionsWithAutocompleteFilter<OrgTeam>({
                  comparator: 'id',
                  createText: (value) => value,
                })}
                options={teams ?? []}
                onChange={(_, value) => {
                  if (typeof value === 'string') {
                    field.onChange(value)
                  } else if (value?.id) {
                    field.onChange(value.id)
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option
                  }
                  return option.name
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
                ListboxComponent={ListBoxInfinite}
                renderOption={(props, option, { selected }) => (
                  <ListItemButton {...(props as HTMLAttributes<HTMLElement>)} key={option.id} selected={selected}>
                    <ListItemText>{option.name || `${t('id')} (${option.id})`}</ListItemText>
                  </ListItemButton>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    label={t('domain:EnvironmentSetting.team')}
                    placeholder={t('domain:EnvironmentSetting.pasteIdOrSelectTeam')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        )}

        <Typography sx={{ flex: 0 }}>{t('domain:EnvironmentSetting.has')}</Typography>

        <Controller
          control={control}
          name="role"
          render={({ field, fieldState, formState }) => (
            <FormControl sx={{ flex: 1 }} required>
              <Select
                {...field}
                input={
                  <OutlinedInput
                    endAdornment={
                      <Box sx={{ mr: 2 }}>
                        <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                      </Box>
                    }
                  />
                }
                renderValue={(selected) => (
                  <CodeInline sx={{ py: 0 }} key={selected}>
                    {selected}
                  </CodeInline>
                )}
              >
                {Object.values(OrgEnvironmentRoleRamMemberRolesEnum).map((role) => (
                  <MenuItem key={role} value={role} selected={field.value.includes(role)}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Typography sx={{ flex: 0 }}>{t('domain:EnvironmentSetting.privileges')}</Typography>
      </Box>
    </DialogFormContent>
  )
}

export const TeamDialogContent = memo(TeamDialogContentRaw)
